import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  getInitFormData,
  applyAutoSlip,
  fnGetIotype,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-enterprise";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnButton from "@shared/components/SnButton";
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_MTL_MPI_REG_001_POP_001 from "@menu/MTL/SN_MTL_MPI_REG_001/SN_MTL_MPI_REG_001_POP_001";
import SN_MTL_MPI_REG_001_POP_002 from "@menu/MTL/SN_MTL_MPI_REG_001/SN_MTL_MPI_REG_001_POP_002";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_MPI_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  const iotypeRef = useRef<any[]>([]);

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "2", "3", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  //#region Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_mpi_reg_001_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      const addedNode = getGridCurrent("grid1").addRow();
      addedNode?.setDataValue("cd_io", addedNode.data?.cd_io);
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }
    getGridCurrent("grid2").deleteSelectedRows();
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_req: nodeData.no_req,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const qtiohChanges = getGridCurrent("grid1").getChange();
      const qtioChanges = getGridCurrent("grid2").getChange();
      const headerInserts = qtiohChanges.insert ?? [];
      const lineInserts = qtioChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        qtiohChanges.modifiedRowcount === 0 &&
        qtioChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_io",
        cd_module: "MTL",
        no_line: 3,
        dt: focusedRowNodeH.data.dt_io,
        girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
        inserts: [qtiohChanges, qtioChanges],
        cd_menu: Menuprops.cd_menu,
      });

      // 요청 데이터 구성
      const SaveData = {
        qtioh: qtiohChanges,
        qtio: qtioChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_MPI_REG_001_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_io);
    } else {
      return;
    }
  };

  //#region onBeforeAddRow1
  const onBeforeAddRow1 = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_io: today,
        cd_io: "top1",
        rt_exch: 1,
        cd_exch: "000",
        tp_slip: "3",
        fg_slip: "2",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };
  //#endregion

  //#region onCellValueChangedH
  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io);
        rowNode.setDataValue("fg_money", ioType.fg_money);
        rowNode.setDataValue("fg_map", ioType.fg_map);

        if (rowCount > 0) {
          gridRefs.current["grid2"].current?.forEachFilteredNode(
            (node: RowNode) => {
              node.setDataValue("cd_io", rowData.cd_io);
              node.setDataValue("fg_io", ioType.fg_io);
              node.setDataValue("fg_money", ioType.fg_money);
              node.setDataValue("fg_map", ioType.fg_map);
            }
          );
        }
        break;
      case "dt_io":
        gridRefs.current["grid2"].current?.forEachFilteredNode(
          (node: RowNode) => {
            node.setDataValue("dt_io", rowData.dt_io);
          }
        );
        break;
    }
  };
  //#endregion

  //#region Grid1BtnClick
  const onGrid1BtnsClick = async (id: string) => {
    // 신규행이 없으면 Dialog로 띄우기
    const qtiohChanges = getGridCurrent("grid1").getChange().insert;
    if (qtiohChanges.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    const requiredValuesCheckH = getGridCurrent("grid1").requiredValuesCheck();
    if (!requiredValuesCheckH) {
      return false;
    }

    // searchPanel에서 선택된 값 전달
    const selectedFormData = searchRef.current.getFormdata();
    const selectedData = {
      cd_biz: selectedFormData.cd_biz,
      dt_from: selectedFormData.dt_from,
      dt_to: selectedFormData.dt_to,
      // cd_io: selectedFormData.cd_io,
      // cd_wh: selectedFormData.cd_wh,
      // cd_dept: selectedFormData.cd_dept,
      // cd_emp: selectedFormData.cd_emp,
    };

    // 신규행 포커스 잡아주기
    let focusedRowHIdx = null;
    gridRefs.current["grid1"].current?.forEachFilteredNode((node: RowNode) => {
      if (node.data.no_io === "" || node.data.no_io === undefined) {
        focusedRowHIdx = node.rowIndex;
      }
    });

    let component = null;
    let popupTitle = "";

    switch (id) {
      case "btn_POP1":
        component = <SN_MTL_MPI_REG_001_POP_001 selectedData={selectedData} />;
        popupTitle = "출고요청적용";
        break;
      case "btn_POP2":
        component = <SN_MTL_MPI_REG_001_POP_002 selectedData={selectedData} />;
        popupTitle = "작업지시적용";
        break;
    }

    const result = await showResizablePopupModal({
      title: popupTitle,
      children: component,
    });

    if (!result || result === "close") return;

    const headerResult = result[0];
    const lineResult = result[1];

    // 요청도움창 Header 결과 적용
    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (focusedRowNodeH && headerResult[0]) {
      for (const key in headerResult[0]) {
        focusedRowNodeH.setDataValue(key, headerResult[0][key]);
      }
    }
    // 요청도움창 Line 결과 적용
    lineResult.map((result: any) => {
      if (headerResult[0]) {
        const addedNode = onAddRow();
        if (addedNode) {
          for (const key in result) {
            switch (key) {
              case "cd_dept":
              case "nm_dept":
              case "cd_emp":
              case "nm_emp":
                break;
              default:
                addedNode.setDataValue(key, result[key]);
            }
          }
          addedNode.setDataValue("yn_req", "Y");
          addedNode.setDataValue("cd_wh", headerResult[0].cd_wh);
          addedNode.setDataValue("nm_wh", headerResult[0].nm_wh);
          addedNode.setDataValue("no_line_req", result.no_line);

          switch (id) {
            case "btn_POP1":
              addedNode.setDataValue("qt_good", result.qt - result.qt_io);
              break;
            case "btn_POP2":
              addedNode.setDataValue("cd_item", result.cd_matl);
              addedNode.setDataValue("nm_item", result.nm_matl);
              addedNode.setDataValue("dc_item_spec", result.dc_item_spec_matl);
              addedNode.setDataValue("dc_item_unit", result.dc_item_unit_matl);
              addedNode.setDataValue("qt_good", result.qt_need);
              addedNode.setDataValue("no_req", result.no_wo);
              addedNode.setDataValue("no_order", result.no_wo);
              addedNode.setDataValue("no_line_order", result.no_line);
              break;
          }
          getGridCurrent("grid2").applyBlock(addedNode);
        }
      }
    });
  };
  //#endregion

  //#region onBeforeAddRow2
  const onBeforeAddRow2 = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const updatedRow = {
        cd_firm: focusedNodeH.data?.cd_firm,
        no_io: focusedNodeH.data?.no_io,
        cd_biz: focusedNodeH.data?.cd_biz,
        cd_io: focusedNodeH.data?.cd_io,
        dt_io: focusedNodeH.data?.dt_io,
        no_line: maxNoLine + 1,
        fg_slip: focusedNodeH.data.fg_slip,
        tp_slip: focusedNodeH.data.tp_slip,
        fg_io: focusedNodeH.data.fg_io,
        fg_money: focusedNodeH.data.fg_money,
        fg_map: focusedNodeH.data.fg_map,
        fg_vat: "11",
        rt_exch: focusedNodeH.data?.rt_exch,
        qt: 0,
        qt_good: 0,
        qt_bad: 0,
        cd_wh: "",
        nm_wh: "",
        yn_req: "N",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };
  //#endregion

  //#region onCellValueChangedL
  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "cd_wh") {
      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();
      if (rowCount === 1) return;
      if (!rowData.cd_wh) return;

      let apply = false;
      gridRefs.current["grid2"].current?.forEachFilteredNode(
        (node: RowNode) => {
          if (
            node.data[colId] === "" ||
            node.data[colId] === null ||
            node.data[colId] === undefined
          )
            apply = true;
        }
      );
      if (!apply) return;

      const confirm = await showDialog(
        "confirm",
        "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
      );

      if (confirm) {
        gridRefs.current["grid2"].current?.forEachFilteredNode(
          (node: RowNode) => {
            if (
              node.data.cd_wh === "" ||
              node.data.cd_wh === null ||
              node.data.cd_wh === undefined
            ) {
              node.setDataValue("cd_wh", rowData.cd_wh);
              node.setDataValue("nm_wh", rowData.nm_wh);
            }
          }
        );
      }
    }

    switch (colId) {
      case "qt_good":
      case "qt_bad":
        rowNode.setDataValue("qt", rowData.qt_good + rowData.qt_bad);
        break;
    }
  };
  //#endregion

  //#region grid2Btns
  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onCellValueChanged: onCellValueChangedH,
          onBeforeAddRow: onBeforeAddRow1,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onBeforeAddRow: onBeforeAddRow2,
          onCellValueChanged: onCellValueChangedL,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "출고정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_io"] }}
            header={
              <div>
                <SnButton
                  text={"요청적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btn_POP1");
                  }}
                />
                <SnButton
                  text={"작업지시적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btn_POP2");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{ rowSelection: "multiple", title: "상세정보" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_MPI_REG_001;
